import { useApiPolling } from "Hooks/useApiPolling";
import { esignworkflowstageOptions, esignworkflowstatusOptions } from "Models/Enums";
import moment from "moment";
import React, { Fragment, useState } from "react";
import {  Container, Divider, Grid, Header, Icon, Segment } from "semantic-ui-react";
import { Button, Colors, Display, Sizes } from "Views/Components/Button/Button";
import Modal from "Views/Components/Modal/Modal";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { ESignWorkflowSummary } from "./ESignEnevlopeList";
import axios, { AxiosError } from "axios";
import { SERVER_URL } from "Constants";
import { ESignSignedDocument } from "./EnvelopeDetailSections/ESignSignedDocument";
import { SigneeDetails } from "./EnvelopeDetailSections/SigneeDetails";

const fetchRecentESignEnvelope = async (workflowId: string): Promise<ESignWorkflowSummary | undefined> => {
	if (!workflowId) {
		return undefined;
	}
	try {
		const response = await axios.get(`${SERVER_URL}/api/esign/workflow/${workflowId}`);
		return response.data;
	} catch (error) {
		throw error as AxiosError;
	}
}

type Props = {
	workflowSummary: ESignWorkflowSummary;
	onClose: () => void;
}

export const ESignEnvelopeDetails: React.FunctionComponent<Props> = ({ workflowSummary, onClose }) => {
	const DELAY_TIME = 60000;
	const [showSignedDocument, setShowSignedDocument] = useState(false);

	// fetch the latest data for the selected envelope
	const {data: summary, forceRefresh } = useApiPolling(
		() => fetchRecentESignEnvelope(workflowSummary.id),
		DELAY_TIME,
		workflowSummary.status === 'COMPLETED'
	);

	const hasDocumentCompleted = summary && summary.status === 'COMPLETED' && summary.base64SignedContract.length > 0;

	return (
		<Modal
			isOpen
			onRequestClose={onClose}
			label=""
			className="steps"
		>
			<div key="header" className="step__header">
				<div className="step__header-item">
					<h2 key="header-item" className="modal__title">Workflow Details</h2>
					{!hasDocumentCompleted && (
						<Button
							key="header-refresh"
							icon={{ icon: 'refresh-cw', iconPos: 'icon-only' }}
							display={Display.Text}
							colors={Colors.Secondary}
							sizes={Sizes.ExtraLarge}
							onClick={() => forceRefresh()}
							labelVisible={false}
						/>
					)}
				</div>
				<div className="step__header-item">
					<Button
						key="header-cancel"
						icon={{ icon: 'close', iconPos: 'icon-only' }}
						display={Display.Solid}
						colors={Colors.Error}
						sizes={Sizes.ExtraLarge}
						onClick={onClose}
						labelVisible={false}
					/>
				</div>
			</div>
			{!summary && (
				<InlineSpinner />
			)}
			{summary && (
				<>
					<Segment fluid="true" className="workflow-summary-card">
						<Header as="h3">
							<div>
								<Icon name="file alternate outline" /> Document Name: {summary.template.name}
								{hasDocumentCompleted && (
									<Button
										display={Display.Text}
										colors={Colors.Primary}
										onClick={() => setShowSignedDocument(true)}
									>
										View Signed Document
									</Button>
								)}
							</div>
						</Header>
						<Divider />
						<Grid columns={2} divided className="workflow-summary-details">
							<Grid.Row>
								<Grid.Column>
									<Container>
										<Icon
											name="calendar alternate outline"
										/> Date Created: {moment(summary.created).format('MM/DD/YYYY - hh:mm A')}
									</Container>
								</Grid.Column>
								<Grid.Column>
									<Container>
										<Icon
											name="edit outline"
										/> Last Modified: {moment(summary.modified).format('MM/DD/YYYY - hh:mm A')}
									</Container>
								</Grid.Column>
							</Grid.Row>
							<Grid.Row>
								<Grid.Column>
									<Container>
										<Icon
											name="flag outline"
										/> Marked: {esignworkflowstatusOptions[summary.status]}
									</Container>
								</Grid.Column>
								<Grid.Column>
									<Container>
										<Icon
											name="history"
										/> Last Activity: {esignworkflowstageOptions[summary.stage]}
									</Container>
								</Grid.Column>
							</Grid.Row>
							<Grid.Row>
								<Grid.Column>
									<Container>
										<Icon name="check circle outline" /> Signed By: {summary.signatoryCount}
									</Container>
								</Grid.Column>
								<Grid.Column>
									<Container>
										<Icon name="times circle outline" /> Rejected By: {summary.rejectionCount}
									</Container>
								</Grid.Column>
							</Grid.Row>
							<Grid.Row>
								<Grid.Column>
									<Container>
										<Icon
											name="clock outline"
										/> Expired: {
											moment(summary.created).add(summary.template.lifeSpanInDays, 'days')
												.fromNow()
										}
									</Container>
								</Grid.Column>
							</Grid.Row>
						</Grid>
						<Divider />
						<Grid columns={1} className="signee-details">
							{summary.signees.map((signee, index) => (
								<Fragment key={index}>
									<Grid.Row>
										<Grid.Column>
											<SigneeDetails 
												signee={signee}
												requireWitness={summary.template.requiresWitness}
											/>
										</Grid.Column>
									</Grid.Row>
									{/* Skip the divider if index is last item */}
									{index !== summary.signees.length - 1 && (
										<Divider />
									)}
								</Fragment>
							))}
						</Grid>
						<Divider />
						<Header as="h4">
							<Icon name="envelope" /> Registered Notification Email Address: {summary.notificationEmail}
						</Header>
					</Segment>
					{showSignedDocument && (
						<ESignSignedDocument
							name={summary.template.name}
							base64SignedContract={summary.base64SignedContract}
							onClose={() => setShowSignedDocument(false)}
						/>
					)}
				</>
			)}
		</Modal>
	)
}